import { useCallback, useMemo } from 'react';
import { Sensitive } from './Sensitive';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  MarkerType,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Users, Crown, Shield } from 'lucide-react';

// Custom Node Component
function CustomNode({ data }) {
  const isActive = data.status === 'active';
  const roleColor = data.role === 'admin' ? 'from-violet-500 to-purple-600' 
    : data.role === 'leader' ? 'from-blue-500 to-[#1E3FBF]' 
    : 'from-[#2447D6] to-teal-600';

  const RoleIcon = data.role === 'admin' ? Crown : data.role === 'leader' ? Shield : Users;

  return (
    <div
      onClick={() => data.onNodeClick && data.onNodeClick(data)}
      className={`
        min-w-[220px] max-w-[280px] p-3 rounded-xl border-2 cursor-pointer
        transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105
        ${isActive 
          ? 'bg-white border-[#2447D6]/50 hover:border-[#2447D6]' 
          : 'bg-[#E9EDF7] border-[#E9EDF7] opacity-60'
        }
      `}
      data-testid={`org-node-${data.id}`}
    >
      {/* React Flow Handles for connections */}
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      
      {/* Header with Role Icon */}
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg bg-gradient-to-br ${roleColor}`}>
          <RoleIcon className="h-3.5 w-3.5 text-white" />
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
          isActive 
            ? 'bg-emerald-100 text-emerald-700' 
            : 'bg-slate-200 text-[#2B3674]/80'
        }`}>
          {isActive ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Name */}
      <div className="font-bold text-sm text-[#2B3674] mb-1 truncate">
        {data.name}
      </div>

      {/* Info */}
      <div className="flex items-center justify-between text-[11px] text-[#A3AED0]">
        <span className="capitalize font-medium">{data.role}</span>
        <span className="font-bold text-[#1E3FBF]"><Sensitive>{data.comp_percentage}%</Sensitive></span>
      </div>

      {/* Team Size */}
      {data.team_size > 0 && (
        <div className="mt-2 pt-2 border-t border-[#E9EDF7]">
          <div className="text-[10px] text-[#A3AED0] flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span>{data.team_size} team member{data.team_size !== 1 ? 's' : ''}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const nodeTypes = {
  custom: CustomNode,
};

export default function OrgChart({ data, onNodeClick }) {
  // Build nodes and edges from hierarchy data
  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes = [];
    const edges = [];
    const levelCounts = {};

    // Recursively build nodes and edges
    const buildNodes = (node, level = 0, parentId = null) => {
      if (!levelCounts[level]) levelCounts[level] = 0;
      const xPosition = levelCounts[level] * 320;
      const yPosition = level * 180;
      levelCounts[level]++;

      nodes.push({
        id: node.id,
        type: 'custom',
        position: { x: xPosition, y: yPosition },
        data: {
          ...node,
          team_size: node.children?.length || 0,
          onNodeClick,
        },
      });

      if (parentId) {
        edges.push({
          id: `${parentId}-${node.id}`,
          source: parentId,
          target: node.id,
          type: 'smoothstep',
          animated: node.status === 'active',
          style: { 
            stroke: node.status === 'active' ? '#22d3ee' : '#94a3b8',
            strokeWidth: 3,
          },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: node.status === 'active' ? '#22d3ee' : '#94a3b8',
            width: 25,
            height: 25,
          },
          label: '',
          labelBgStyle: { fill: 'transparent' },
        });
      }

      if (node.children && node.children.length > 0) {
        node.children.forEach((child) => buildNodes(child, level + 1, node.id));
      }
    };

    if (data?.trees) {
      data.trees.forEach((tree) => buildNodes(tree));
    }

    return { nodes, edges };
  }, [data, onNodeClick]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const onNodeDragStop = useCallback((event, node) => {
    // Custom logic when node drag stops (optional)
  }, []);

  if (!data?.trees || data.trees.length === 0) {
    return (
      <div className="h-[600px] flex items-center justify-center bg-[#F4F7FE] rounded-xl border border-[#E9EDF7]">
        <div className="text-center">
          <Users className="h-12 w-12 mx-auto text-[#A3AED0] mb-3" />
          <p className="text-[#A3AED0] text-sm">No organization data to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[600px] border border-[#E9EDF7] rounded-xl overflow-hidden bg-white">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.2}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        className="bg-white"
        proOptions={{ hideAttribution: true }}
      >
        <Background color="#cbd5e1" gap={20} size={1} className="bg-[#E9EDF7]" />
        <Controls className="bg-white border border-[#E9EDF7] rounded-lg shadow-lg" />
      </ReactFlow>
    </div>
  );
}
